package fr.frux.core.ads

import fr.frux.core.common.logging.Logger
import fr.frux.core.common.prefs.AppPreferences
import fr.frux.core.common.result.runCatchingCancellable
import kotlin.time.Clock
import kotlin.time.Duration.Companion.minutes

/**
 * Session publicitaire (ADR 0030 point 5 : rien en première session).
 *
 * Une nouvelle session commence au démarrage du process ou au retour au premier plan, si le dernier
 * passage en arrière-plan est inconnu ou date de [SESSION_GAP] ou plus. Sinon la session continue,
 * y compris après une mort du process dans la fenêtre. L'Activity de la régie vit dans le même
 * process : son affichage n'est pas un passage en arrière-plan.
 *
 * L'horloge murale suffit ici : un écart d'horloge ne déplace qu'une frontière de session, jamais un
 * droit payé (ceux-là s'ancrent sur le serveur, cf. `AccountEntitlements.serverClock`).
 *
 * Préférences illisibles : on rend la session 1 sans rien écrire, ce qui ferme la publicité plutôt
 * que d'en montrer à tort en première session.
 */
class AdSessionTracker(
    private val prefs: AppPreferences,
    private val clock: Clock,
) {
    private var current: Int? = null
    private var inForeground = false

    /** Démarrage du process ou retour au premier plan. Rend l'index de la session courante. */
    suspend fun onForeground(): Int {
        val ongoing = current
        if (ongoing != null && inForeground) return ongoing
        inForeground = true
        val index =
            runCatchingCancellable { resolveSession() }
                .getOrElse { error ->
                    Logger.e(TAG, "Ad session unreadable, treated as first session", error)
                    FIRST_SESSION
                }
        current = index
        return index
    }

    /** Passage en arrière-plan : point de départ de la fenêtre de [SESSION_GAP]. */
    suspend fun onBackground() {
        inForeground = false
        runCatchingCancellable {
            prefs.putString(KEY_LAST_BACKGROUND_AT, clock.now().toEpochMilliseconds().toString())
        }.onFailure { error -> Logger.e(TAG, "Ad session: background time not saved", error) }
    }

    /**
     * Numéro de la session où l'interstitiel a été vu, `null` si jamais. Préférences illisibles : lève,
     * et l'appelant ferme l'interstitiel plutôt que de le montrer deux fois.
     */
    suspend fun interstitialShownSession(): Int? =
        // Valeur corrompue : traitée comme « vu », jamais comme « pas encore vu ».
        prefs.getString(KEY_INTERSTITIAL_SESSION)?.let { it.toIntOrNull() ?: Int.MAX_VALUE }

    /** Consigne l'interstitiel vu, avant son affichage : une mort du process pendant l'annonce ne le réarme pas. */
    suspend fun markInterstitialShown(session: Int) {
        runCatchingCancellable { prefs.putString(KEY_INTERSTITIAL_SESSION, session.toString()) }
            .onFailure { error -> Logger.e(TAG, "Ad session: interstitial not saved", error) }
    }

    private suspend fun resolveSession(): Int {
        // Valeur illisible ou hors bornes : on repart de zéro, donc de la première session, sans pub.
        val count = prefs.getString(KEY_SESSION_COUNT)?.toIntOrNull()?.takeIf { it in 1..MAX_SESSION_COUNT } ?: 0
        val lastBackground = prefs.getString(KEY_LAST_BACKGROUND_AT)?.toLongOrNull()
        val now = clock.now().toEpochMilliseconds()
        // Un passage en arrière-plan daté du futur (horloge reculée) ne prolonge pas la session.
        val continues =
            count > 0 &&
                lastBackground != null &&
                now >= lastBackground &&
                now - lastBackground < SESSION_GAP.inWholeMilliseconds
        if (continues) return count
        val next = count + 1
        prefs.putString(KEY_SESSION_COUNT, next.toString())
        Logger.i(TAG, "Ad session started: index=$next")
        return next
    }

    companion object {
        val SESSION_GAP = 30.minutes
        const val FIRST_SESSION = 1
        private const val MAX_SESSION_COUNT = 10_000
        internal const val KEY_SESSION_COUNT = "ads_session_count"
        internal const val KEY_LAST_BACKGROUND_AT = "ads_last_background_at"
        internal const val KEY_INTERSTITIAL_SESSION = "ads_interstitial_session"
        private const val TAG = "AdSession"
    }
}
